/**
 * Dates for the unified Reports list (SE-6984).
 *
 * **The list mixes two different kinds of value, and they are not
 * interchangeable.** A match report carries `Match.scheduledDate`, a CIVIL date
 * (`YYYY-MM-DD`) at the venue with no instant behind it. A single-player report
 * carries `createdAt`, a real instant. Treating either as the other is what
 * produced both defects this module exists to fix:
 *
 *  - `new Date("2026-02-14")` is UTC midnight, so every viewer west of
 *    Greenwich read the row as the 13th. The date a match was played is not a
 *    property of where the reader is sitting.
 *  - sorting compared the two as TEXT, and `"2026-02-14"` sorts before
 *    `"2026-02-14T20:00:00.000Z"` purely because it is a prefix — so a report
 *    filed on the day of a match never landed beside it.
 *
 * Instants are rendered in **UTC**, deliberately. The list shows a date, not a
 * time, and two colleagues in different countries discussing the same row have
 * to see the same one; a local render would give them different answers about
 * the same report.
 * @module features/scouting/utils/reportDate
 */

/** Matches a civil date with no time part. */
const CIVIL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Month names, indexed 1–12 with a dead slot so the maths reads plainly. */
const MONTHS = [
  "",
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
] as const;

/**
 * The `YYYY-MM-DD` a value falls on, for comparing and grouping.
 *
 * A civil date is already that. An instant is reduced to its UTC calendar day,
 * which is the same day this module renders — so the order the list is sorted
 * in and the dates it displays can never disagree.
 * @param value - A civil date or an ISO instant.
 * @returns The calendar day, or null when the value is not a date at all.
 */
export const toDateKey = (value: string | null): string | null => {
  if (value === null) return null;
  if (CIVIL_DATE.test(value)) {
    // Shape is not validity: "2026-02-30" matches the pattern and would
    // format as a date that never existed. UTC round-trip proves the
    // calendar agrees.
    const [year, month, day] = value.split("-").map(Number);
    const roundTrip = new Date(Date.UTC(year, month - 1, day));

    return roundTrip.getUTCMonth() === month - 1 &&
      roundTrip.getUTCDate() === day
      ? value
      : null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  return parsed.toISOString().slice(0, 10);
};

/**
 * Formats a report's date for the meta line.
 * @param value - A civil date or an ISO instant, or null.
 * @param emDash - What to render when there is no date.
 * @returns "14 Feb 2026", or the placeholder.
 */
export const formatReportDate = (
  value: string | null,
  emDash: string
): string => {
  const key = toDateKey(value);
  if (key === null) return emDash;

  const match = CIVIL_DATE.exec(key);
  if (match === null) return emDash;

  // Built from the parts rather than through `toLocaleDateString`, which
  // resolves against the machine's zone — the very thing that shifted the day.
  return `${match[3]} ${MONTHS[Number(match[2])]} ${match[1]}`;
};
